import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { unwrapResult } from '@reduxjs/toolkit';
import { object, string } from 'yup';

import ROUTES from 'routes';
import { Color } from 'types/colors';
import { OAuthProviderName } from 'types';
import { useAppDispatch, useAppSelector, useDocumentTitle } from 'hooks';
import { login } from 'redux/auth/actions';
import store from 'redux/redux-store';
import { createOAuthUrl } from 'redux/oauth/actions';
import { selectAccountStatus, selectAccountTokenStatus } from 'redux/account/selectors';
import { Form, FormFieldWithError, FormFooter, Spinner, TextField } from 'components/ui';
import Button from 'components/ui/Button';
import Tile from 'components/ui/Tile';
import Typography from 'components/ui/Typography';
import Icon from 'components/Icon';
import { SpriteIcon } from 'types/icons';
import AlertBox from 'components/AlertBox';
import AccountInactiveError from 'pages/LoginPage/components/AccountInactiveError';
import AccountSuspendedError from 'pages/LoginPage/components/AccountSuspendedError';

import css from './LoginForm.module.scss';

interface FormValues {
    login: string;
    password: string;
}

interface FormStatus {
    error?: {
        code: string;
        message: string;
    };
}

const ERR_CODE_INVALID_CREDENTIALS = 'invalid_login_credentials';

const LoginForm: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    useDocumentTitle('Login');

    const { values, errors, status, handleSubmit, handleChange, isSubmitting, setSubmitting, setStatus } =
        useFormik<FormValues>({
            validationSchema: object().shape({
                login: string().required(),
                password: string().required(),
            }),
            validateOnBlur: false,
            validateOnChange: false,
            initialValues: {
                login: '',
                password: '',
            },
            onSubmit: () => {
                store
                    .dispatch(login({ username: values.login, password: values.password }))
                    .then(unwrapResult)
                    .catch((error) => {
                        setSubmitting(false);
                        setStatus({ error: { code: error.error.code, message: error.error.message } as FormStatus });
                    });
            },
        });

    const accountStatus = useAppSelector(selectAccountStatus);
    const tokenStatus = useAppSelector(selectAccountTokenStatus);

    const isAccountInactive = accountStatus === 'deleted' || tokenStatus === 'inactive_account';
    const isAccountSuspended = status?.error?.code === 'account_suspended';

    type OAuthButtonItem = { icon: SpriteIcon; color: Color; provider: OAuthProviderName };

    const oAuthButtons: OAuthButtonItem[] = [
        { icon: 'vk-circle-filled', color: 'vk-blue', provider: OAuthProviderName.VK },
        { icon: 'facebook-circle-filled', color: 'fb-blue', provider: OAuthProviderName.FACEBOOK },
        { icon: 'google-circle-filled', color: 'red', provider: OAuthProviderName.GOOGLE },
        { icon: 'apple-circle-filled', color: 'black', provider: OAuthProviderName.APPLE },
    ];

    if (isAccountSuspended) {
        return <AccountSuspendedError />;
    }

    if (isAccountInactive) {
        return <AccountInactiveError />;
    }

    return (
        <>
            <div className={clsx(css.container)}>
                {isSubmitting ? (
                    <Spinner color="white" />
                ) : (
                    <>
                        <div className={css.container}>
                            <Tile>
                                <Tile.Header>{t('Onboarding.Signin.Title')}</Tile.Header>
                                <Tile.Item className="space-y-8">
                                    <div className={css.oauth}>
                                        {oAuthButtons.map((item) => (
                                            <div
                                                className={css.oauthButton}
                                                key={item.provider}
                                                onClick={() => dispatch(createOAuthUrl(item.provider))}
                                            >
                                                <Icon name={item.icon} width={47} color={item.color} />
                                            </div>
                                        ))}
                                    </div>

                                    {status?.error && <AlertBox message={status?.error.message} />}

                                    <Form onSubmit={handleSubmit}>
                                        <FormFieldWithError
                                            errorMessage={errors?.login}
                                            isVisible={Boolean(errors.login)}
                                        >
                                            <TextField
                                                autoFocus
                                                autoComplete="off"
                                                type="text"
                                                name="login"
                                                value={values.login}
                                                onChange={handleChange}
                                                error={Boolean(errors.login)}
                                                placeholder={t('LoginForm.EmailOrUsername')}
                                            />
                                        </FormFieldWithError>
                                        <FormFieldWithError
                                            errorMessage={errors?.password}
                                            isVisible={Boolean(errors.password)}
                                        >
                                            <TextField
                                                type="password"
                                                name="password"
                                                value={values.password}
                                                onChange={handleChange}
                                                error={Boolean(errors.password)}
                                                placeholder={t('Common.Password')}
                                            />
                                        </FormFieldWithError>
                                        <FormFooter direction="column">
                                            <Button withPadding type="submit" disabled={isSubmitting} blocked>
                                                {t('LoginForm.LogInButtonText')}
                                                {isSubmitting ? '...' : null}
                                            </Button>
                                            {/* HACK: validateOnBlur + links https://github.com/formium/formik/issues/1796#issuecomment-606643671 */}
                                            <Link
                                                to={ROUTES.RESET_PASSWORD}
                                                onMouseDown={(event) => {
                                                    event.preventDefault();
                                                }}
                                            >
                                                <Button type="button" color="transparent">
                                                    {t('LoginForm.ForgotPassword')}
                                                </Button>
                                            </Link>
                                        </FormFooter>
                                    </Form>
                                </Tile.Item>
                            </Tile>
                        </div>
                        <div className={css.signUpBlock}>
                            <Typography
                                align="center"
                                color="white"
                                style="BodyMedSemibold"
                                className={
                                    (status?.error?.code === ERR_CODE_INVALID_CREDENTIALS && css.margin) || undefined
                                }
                            >
                                {t('LoginForm.NotMemberYet')}
                            </Typography>
                            <Button blocked withPadding color="white" onClick={() => navigate(ROUTES.JOIN)}>
                                {t('Onboarding.Signup.Signup')}
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default LoginForm;
